import { CustomProperty, values } from "../types/types";

export const getCalculatedValue = (
  prop: CustomProperty,
  customProps: CustomProperty[],
  currency?: string
): values | null => {
  // Find the operator (+, -, *, /)
  const operatorMatch = prop.value.match(/[\+\-\*\/]/)?.[0];

  if (!operatorMatch) {
    return {
      title: prop.name,
      value: "0",
    };
  }

  // Extract field names - handle both [brackets] and plain field names
  let fieldNames: string[] = [];

  // Try with brackets first: [hours]*[price]
  const bracketsMatch = prop.value.match(/\[([^\]]+)\]/g);
  if (bracketsMatch) {
    fieldNames = bracketsMatch.map((field: any) =>
      field.replace(/[\[\]]/g, "")
    );
  } else {
    // No brackets, split by operator: hours*price
    fieldNames = prop.value
      .split(operatorMatch)
      .map((name: string) => name.trim());
  }

  // Check if any of the fields used in calculation are currency type
  const hasCurrencyField = fieldNames.some((fieldName: string) => {
    const matchingProp = customProps.find(
      (customProp: any) => customProp.name === fieldName
    );
    return matchingProp?.type === "currency";
  });

  // Get the numeric values for each field
  const fieldValues: number[] = fieldNames.map((fieldName: string) => {
    const matchingProp = customProps.find(
      (customProp: any) => customProp.name === fieldName
    );
    // Try parsing as number, handle both string and number values
    const rawValue = matchingProp?.value;
    const numValue =
      typeof rawValue === "number"
        ? rawValue
        : parseFloat(String(rawValue || "0"));
    return isNaN(numValue) ? 0 : numValue;
  });

  if (fieldValues.length === 0 || fieldValues.every((val) => val === 0)) {
    return {
      title: prop.name,
      value: "0",
    };
  }

  // For a single value, just return it
  if (fieldValues.length === 1) {
    const displayValue =
      hasCurrencyField && currency
        ? `${fieldValues[0]} ${currency}`
        : fieldValues[0].toString();
    return {
      title: prop.name,
      value: displayValue,
    };
  }

  // Start with the first value and apply the operator to subsequent values
  const calculatedValue = fieldValues
    .slice(1)
    .reduce((acc: number, val: number) => {
      switch (operatorMatch) {
        case "+":
          return acc + val;
        case "-":
          return acc - val;
        case "*":
          return acc * val;
        case "/":
          return val !== 0 ? acc / val : acc;
        default:
          return acc + val;
      }
    }, fieldValues[0]);

  // Add currency suffix if any field in the calculation is currency type
  const displayValue =
    hasCurrencyField && currency
      ? `${calculatedValue} ${currency}`
      : calculatedValue.toString();

  return {
    title: prop.name,
    value: displayValue,
  };
};

export const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const isToday = date.toDateString() === now.toDateString();
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) {
    return `today at ${date.toLocaleTimeString("da-DK", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else if (isYesterday) {
    return `yesterday at ${date.toLocaleTimeString("da-DK", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else {
    return date.toLocaleString("da-DK", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
};
